import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";

import { bindActionCreators } from "redux";

import { FilterSection, FilterItem, FilterItemField, DataColumnDef, RequiredState, PickerInput } from "@plex/react-components";

import { IKeyData } from "infrastructure";
import { useLocalizedValues, useLocalizerWithFallback, useLocalizer } from "ui/hooks";
import { useUserContext } from "ui/hooks";
import {
  ITenantsList,
  ITenant,
  ITenantsState,
  tenantActions,
  IEnterprisesState,
  ISecurityRolesList,
  ISecurityRoleSearchCriterion,
  ISecurityRolesState,
  roleActions,
  ISecurityRole,
  ITenantSearchCriterion
} from "ui/screens/security/models";
import { securityMessages } from "ui/screens/security/security.localization";
import { createPickerSearchCriterion, IPickerState } from "ui/screens/security/shared/picker";

import { securityRoleMembersActions } from "../SecurityRoleMembers.actions";
import { ISecurityRoleMembersFilter, ISecurityRoleMembersState } from "../SecurityRoleMembers.state";

export interface ISecurityRoleMembersFilterProps {
  tenants: ITenantsList;
  securityRoles: ISecurityRolesList;
  selectedTenantId?: string | null;
  selectedSecurityRoleId?: string | null;
  applyFilter: (filter: ISecurityRoleMembersFilter) => void;
  loadTenants: (request: IKeyData<ITenantSearchCriterion>) => void;
  loadSecurityRoles: (criterion: IKeyData<ISecurityRoleSearchCriterion>) => void;
  clearTenants: (criterion: IKeyData) => void;
  clearSecurityRoles: (criterion: IKeyData) => void;
}

export const tenantPicker = "security-role-members-filter-tenant-picker";
export const securityRolePicker = "security-role-members-filter-security-role-picker";

export const SecurityRoleMembersFilter: React.FC<ISecurityRoleMembersFilterProps> = ({
  applyFilter,
  tenants,
  loadTenants,
  securityRoles,
  loadSecurityRoles,
  selectedTenantId,
  selectedSecurityRoleId,
  clearSecurityRoles
}) => {
  const localizedValues = useLocalizedValues(() => ({
    tenant: securityMessages.filter("tenant"),
    account: securityMessages.filter("account"),
    enterprise: securityMessages.filter("enterprise"),
    securityRole: securityMessages.filter("securityRole")
  }));
  const localizeWithFallback = useLocalizerWithFallback();
  const localize = useLocalizer();
  const userContext = useUserContext();

  const [state, setState] = useState<{
    criteria: ISecurityRoleMembersFilter;
    autoSearch: boolean;
    pinned: boolean;
  }>({
    criteria: {},
    autoSearch: false,
    pinned: false
  });

  const [tenantPickerState, setTenantPickerState] = useState<IPickerState<ITenant>>({
    selectedItems: []
  });

  const [securityRolesPickerState, setSecurityRolesPickerState] = useState<IPickerState<ISecurityRole>>({
    selectedItems: []
  });

  const selectSecurityRole = useCallback((selected: ISecurityRole[]) => {
    setSecurityRolesPickerState(prev => ({ ...prev, selectedItems: selected }));
  }, []);

  const selectTenant = useCallback(
    (selected: ITenant[]) => {
      setTenantPickerState(prev => ({ ...prev, selectedItems: selected }));
      selectSecurityRole([]);
      clearSecurityRoles({ id: securityRolePicker } as IKeyData);
    },
    [selectSecurityRole, clearSecurityRoles]
  );

  useEffect(() => {
    if (!tenantPickerState.isLoaded && loadTenants) {
      loadTenants({ id: tenantPicker, data: { ...createPickerSearchCriterion() } });
      setTenantPickerState(prev => ({ ...prev, isLoaded: true }));
    }

    if (!tenantPickerState.initialized && tenants.initialized && selectedTenantId) {
      setTenantPickerState(prev => ({ ...prev, selectedItems: tenants.items.filter(e => e.tenantId === selectedTenantId), initialized: true }));
    }
  }, [loadTenants, selectedTenantId, tenants.items, tenantPickerState.isLoaded, tenantPickerState.initialized, tenants.initialized]);

  useEffect(() => {
    if (!securityRolesPickerState.isLoaded && loadSecurityRoles && tenantPickerState.selectedItems.length) {
      loadSecurityRoles({
        id: securityRolePicker,
        data: {
          tenantId: tenantPickerState.selectedItems[0]?.tenantId,
          ...createPickerSearchCriterion(),
          defaultValueProvider: row => localize({ value: row.name })
        }
      });
      setSecurityRolesPickerState(prev => ({ ...prev, isLoaded: true }));
    }

    if (!securityRolesPickerState.initialized && securityRoles.initialized && selectedSecurityRoleId) {
      setSecurityRolesPickerState(prev => ({ ...prev, selectedItems: securityRoles.items.filter(e => e.securityRoleId === selectedSecurityRoleId), initialized: true }));
      setState(prev => ({ ...prev, autoSearch: true }));
    }
  }, [
    loadSecurityRoles,
    selectedSecurityRoleId,
    securityRoles.items,
    securityRolesPickerState.isLoaded,
    securityRolesPickerState.initialized,
    securityRoles.initialized,
    tenantPickerState.selectedItems,
    localize
  ]);

  useEffect(() => {
    if (state.autoSearch) {
      applyFilter({ tenant: tenantPickerState.selectedItems[0], securityRole: securityRolesPickerState.selectedItems[0] });
      setState(prev => ({ ...prev, autoSearch: false }));
    }
  }, [state.autoSearch, applyFilter, tenantPickerState.selectedItems, securityRolesPickerState.selectedItems]);

  return (
    <FilterSection
      onSearch={() => {
        applyFilter({ tenant: tenantPickerState.selectedItems[0], securityRole: securityRolesPickerState.selectedItems[0] });
      }}
      pinned={state.pinned}
      onPin={pinned => {
        setState(prevState => ({ ...prevState, pinned }));
      }}
    >
      <FilterItem id={tenantPicker} labelText={localizedValues.tenant} required={RequiredState.required}>
        <FilterItemField shouldCollapse={() => !tenantPickerState.selectedItems.length}>
          <PickerInput
            dialogTitle={localizedValues.tenant}
            errorMessage={localizeWithFallback(tenants.notification?.content[0]?.message?.messageId)}
            onSelectionChanged={selectTenant}
            selected={tenantPickerState.selectedItems}
            keySelector={(row: ITenant) => row.tenantId}
            displaySelector={(row: ITenant) => row.tenantName}
            data={tenants.items}
            onSearch={(query, cols) =>
              loadTenants({
                id: tenantPicker,
                data: {
                  enterpriseId: userContext.getTenants().find(e => e.tenantId === userContext.getTenantId())?.enterpriseId,
                  ...createPickerSearchCriterion(query, cols),
                  query: query
                }
              })
            }
          >
            <DataColumnDef<ITenant> id="tenantName" title={localizedValues.tenant} valueSelector={row => row.tenantName} searchable>
              {row => row.tenantName}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem id={securityRolePicker} labelText={localizedValues.securityRole} required={RequiredState.required}>
        <FilterItemField>
          <PickerInput
            disabled={!tenantPickerState.selectedItems[0]?.tenantId}
            dialogTitle={localizedValues.securityRole}
            errorMessage={localizeWithFallback(securityRoles.notification?.content[0]?.message?.messageId)}
            onSelectionChanged={selectSecurityRole}
            keySelector={row => row.securityRoleId}
            displaySelector={row => localize({ value: row.name })} // What actually gets shown in the picker
            selected={securityRolesPickerState.selectedItems}
            data={securityRoles.items}
            onSearch={(query, cols) => {
              loadSecurityRoles({
                id: securityRolePicker,
                data: {
                  tenantId: tenantPickerState.selectedItems[0]?.tenantId,
                  enterpriseId: userContext.getTenants().find(e => e.tenantId === userContext.getTenantId())?.enterpriseId,
                  ...createPickerSearchCriterion(query, cols),
                  query: query,
                  defaultValueProvider: row => localize({ value: row.name })
                }
              });
            }}
          >
            <DataColumnDef<ISecurityRole>
              id="roleName"
              title={localizedValues.securityRole}
              valueSelector={row => {
                return localize({ value: row.name });
              }}
              searchable
            >
              {row => {
                return localize({ value: row.name });
              }}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { securityRoleMembers: ISecurityRoleMembersState; tenants: ITenantsState; enterprises: IEnterprisesState; roles: ISecurityRolesState }) => {
    return {
      tenants: state.tenants[tenantPicker] || { items: [] },
      securityRoles: state.roles[securityRolePicker] || { items: [] }
    };
  },
  dispatch => {
    return bindActionCreators(
      {
        applyFilter: securityRoleMembersActions.applyFilter,
        loadTenants: tenantActions.loadTenants,
        loadSecurityRoles: roleActions.loadSecurityRoles,
        clearTenants: tenantActions.clearTenants,
        clearSecurityRoles: roleActions.clearSecurityRoles
      },
      dispatch
    );
  }
)(SecurityRoleMembersFilter);
